/**
 * Change a book's tier — a comp upgrade, or a downgrade behind an override.
 *
 * CONFIRM-GATED. Nothing is sent until the operator presses the confirm
 * button: opening the dialog, choosing a tier and typing a reason must fire no
 * request at all. That is the invariant `__e2e__/mutations.journey.test.tsx`
 * exists to defend for every mutating dialog in this section.
 *
 * THE TWO REFUSALS ARE PART OF THE FLOW, NOT ERRORS.
 *   · TIER_DOWNGRADE       — the server refuses downgrades by default, mirroring
 *                            tierPolicy's own sentence. The operator ticks the
 *                            override and sends again.
 *   · OVER_CONTRIBUTOR_CAP — the book has more contributors than the
 *                            destination tier allows. Also a tick-and-resend.
 * Both arrive as a WeddingApiError carrying a machine `code`; the dialog
 * branches on the code, never on the message text.
 *
 * WHAT THIS DIALOG MUST SAY OUT LOUD, because an operator cannot infer it:
 *   · no money moves, in either direction — refunds are Stripe's;
 *   · a downgrade switches capabilities OFF (voice, AI polish, video);
 *   · going over the cap does not evict anyone — existing contributors keep
 *     writing, only NEW invitations are blocked;
 *   · the couple's own dashboard can lag by up to a minute.
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Stack,
  Alert,
  AlertTitle,
  Typography,
  Box,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import { WeddingTierChip } from '../WeddingChips';
import { weddingApi, WeddingApiError } from '../weddingApi';
import type { CoupleBookRow, TierChangeResult, TierSpecEntry } from '../weddingApi';
import type { Tier } from '../weddingApi';
import { TIER_META, TIERS, centsToUsd, num } from '../weddingFormat';

const REASON_MIN = 10;
const REASON_MAX = 300;

interface Props {
  open: boolean;
  book: CoupleBookRow | null;
  tierSpec: Record<Tier, TierSpecEntry> | null;
  onClose: () => void;
  onChanged: (result: TierChangeResult) => void;
}

const TierChangeDialog: React.FC<Props> = ({ open, book, tierSpec, onClose, onChanged }) => {
  const [tier, setTier] = React.useState<Tier | ''>('');
  const [reason, setReason] = React.useState('');
  const [overrideDowngrade, setOverrideDowngrade] = React.useState(false);
  const [acknowledgeOverCap, setAcknowledgeOverCap] = React.useState(false);
  const [activateBook, setActivateBook] = React.useState(false);
  const [capWarning, setCapWarning] = React.useState<{ live: number; cap: number } | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);

  // Reset on every open so a previous attempt's override never carries over
  // into the next book — an override that survives is an override nobody
  // consciously granted.
  React.useEffect(() => {
    if (!open) return;
    setTier('');
    setReason('');
    setOverrideDowngrade(false);
    setAcknowledgeOverCap(false);
    setActivateBook(false);
    setCapWarning(null);
    setError(null);
    setSaving(false);
  }, [open, book?.id]);

  if (!book) return null;

  const current = tierSpec ? tierSpec[book.tier] : null;
  const target = tier && tierSpec ? tierSpec[tier] : null;
  const direction: 'up' | 'down' | null =
    current && target ? (target.rank > current.rank ? 'up' : 'down') : null;

  const liveGuests = num(book.live_guests);
  const extras = num(book.extra_contributor_count);
  const targetCap = target ? target.max_contributors + extras : null;
  const wouldExceed = targetCap !== null && liveGuests > targetCap;

  const reasonTooShort = reason.trim().length > 0 && reason.trim().length < REASON_MIN;
  const canSubmit =
    !!tier &&
    tier !== book.tier &&
    reason.trim().length >= REASON_MIN &&
    reason.length <= REASON_MAX &&
    (direction !== 'down' || overrideDowngrade) &&
    (!wouldExceed || acknowledgeOverCap) &&
    !saving;

  const submit = async () => {
    if (!tier || !canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      const result = await weddingApi.changeBookTier(book.id, {
        tier,
        reason: reason.trim(),
        override_downgrade: overrideDowngrade || undefined,
        acknowledge_over_cap: acknowledgeOverCap || undefined,
        activate_book: activateBook || undefined,
      });
      onChanged(result);
      onClose();
    } catch (e) {
      const err = e as WeddingApiError;
      // Branch on the CODE. The prose is for the operator, not for control flow.
      if (err.code === 'TIER_DOWNGRADE') {
        setOverrideDowngrade(false);
        setError(err.message);
      } else if (err.code === 'OVER_CONTRIBUTOR_CAP') {
        // The server's counts win over the row we were rendering from — the
        // book may have gained contributors since this dialog opened.
        const d = err.details || {};
        setCapWarning({
          live: typeof d.live_guests === 'number' ? d.live_guests : liveGuests,
          cap: typeof d.new_cap === 'number' ? d.new_cap : (targetCap ?? 0),
        });
        setError(err.message);
      } else {
        setError(err.message || 'The tier change failed.');
      }
      setSaving(false);
    }
  };

  const showCapWarning = wouldExceed || capWarning !== null;
  const capLive = capWarning ? capWarning.live : liveGuests;
  const capMax = capWarning ? capWarning.cap : (targetCap ?? 0);

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Change tier — {book.wedding_code}
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">Currently</Typography>
            <WeddingTierChip tier={book.tier} />
            {target && (
              <>
                <Typography variant="body2" color="text.secondary">→</Typography>
                <WeddingTierChip tier={tier as Tier} />
              </>
            )}
          </Stack>

          <TextField
            select
            label="New tier"
            value={tier}
            onChange={(e) => {
              setTier(e.target.value as Tier);
              setCapWarning(null);
              setError(null);
              setOverrideDowngrade(false);
              setAcknowledgeOverCap(false);
            }}
            fullWidth
            size="small"
            disabled={saving}
          >
            {TIERS.filter((t) => t !== book.tier).map((t) => (
              <MenuItem key={t} value={t}>
                {TIER_META[t].label}
                {tierSpec && ` — ${centsToUsd(tierSpec[t].price_cents)}, ${tierSpec[t].max_contributors} contributors`}
              </MenuItem>
            ))}
          </TextField>

          {target && current && (
            <Box sx={{ pl: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Contributor cap: {current.max_contributors + extras} → {targetCap}
                {extras > 0 && ` (includes ${extras} purchased extra${extras === 1 ? '' : 's'})`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This book has {liveGuests} contributor{liveGuests === 1 ? '' : 's'} today.
              </Typography>
            </Box>
          )}

          {/* No money moves in EITHER direction. Say it on both paths. */}
          <Alert severity="info" variant="outlined">
            No charge is made and no refund is issued. This is a comp change —
            refunds are handled in Stripe.
          </Alert>

          {direction === 'down' && (
            <Alert severity="warning" variant="outlined">
              <AlertTitle>This is a downgrade</AlertTitle>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Capabilities the lower tier does not include will be switched
                off — voice messages, AI polish and video, depending on the
                tier. The couple keeps every Chapter and photograph already
                collected.
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={overrideDowngrade}
                    onChange={(e) => setOverrideDowngrade(e.target.checked)}
                    disabled={saving}
                  />
                }
                label="I understand, and I am overriding the downgrade guard"
              />
            </Alert>
          )}

          {showCapWarning && (
            <Alert severity="warning" variant="outlined">
              <AlertTitle>Over the contributor cap</AlertTitle>
              <Typography variant="body2" sx={{ mb: 1 }}>
                This book has {capLive} contributors and the new tier allows{' '}
                {capMax}. Nobody is removed and everyone already invited can
                still write — only NEW invitations will be refused until the
                count falls below the cap.
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={acknowledgeOverCap}
                    onChange={(e) => setAcknowledgeOverCap(e.target.checked)}
                    disabled={saving}
                  />
                }
                label="I understand — proceed anyway"
              />
            </Alert>
          )}

          {direction === 'up' && (book.status === 'draft' || book.status === 'pending_payment') && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={activateBook}
                  onChange={(e) => setActivateBook(e.target.checked)}
                  disabled={saving}
                />
              }
              label={`Also activate this book (it is currently ${book.status})`}
            />
          )}

          <TextField
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            size="small"
            disabled={saving}
            error={reasonTooShort || reason.length > REASON_MAX}
            helperText={
              reason.length > REASON_MAX
                ? `${reason.length}/${REASON_MAX} — too long`
                : `${reason.trim().length}/${REASON_MIN} minimum. Recorded in the admin audit log, not shown to the couple.`
            }
          />

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          onClick={submit}
          variant="contained"
          color={direction === 'down' ? 'warning' : 'primary'}
          disabled={!canSubmit}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          {saving ? 'Changing…' : `Change to ${tier ? TIER_META[tier as Tier].label : 'tier'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TierChangeDialog;
